import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import useLoader from 'hooks/useLoader';
import { getMasterEmployee } from 'data/repositories/auth/authentication.repository';
import { changeRunStatus, getRun, saveRun } from 'data/repositories/MigrationRunbook';
import { AuthenticationResponse } from 'data/schemas/auth';
import { MigrationRun, RunbookSection, Visibility } from 'data/schemas/MigrationRunbook';
import { MultiEmployeePicker } from 'presentations/view/TeamCalendar/components/EmployeePicker';
import RunbookPlanBuilder from './RunbookPlanBuilder';
import { createDefaultSections, createStep } from './runbookPlan.helpers';
import useRunbookIdentity, { actor } from './useRunbookIdentity';

const visibilityOptions: Array<{ value: Visibility; title: string; detail: string }> = [
    { value: 'only_me', title: 'Only me', detail: 'Private draft and execution.' },
    {
        value: 'selected_people',
        title: 'Selected people',
        detail: 'Choose specific coworkers who can open this run.'
    },
    {
        value: 'selected_teams',
        title: 'Selected teams',
        detail: 'Everyone in the chosen teams can view it.'
    },
    {
        value: 'all_menu_users',
        title: 'All menu users',
        detail: 'Visible to every signed-in user with this menu.'
    }
];

const normalizeTeams = (value?: string) =>
    (value ?? '')
        .split(',')
        .map(item => item.trim().toLowerCase())
        .filter(Boolean);

const toLocalInput = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return localDate.toISOString().slice(0, 16);
};
const toApiDate = (value?: string) => (value ? new Date(value).toISOString() : undefined);
const signatureText = (value?: string) => (value ?? '').trim();

const planSignature = (sections: RunbookSection[]) =>
    JSON.stringify(
        sections.map(section => ({
            sectionType: section.sectionType,
            title: signatureText(section.title),
            sequence: section.sequence,
            steps: section.steps.map(step => ({
                title: signatureText(step.title),
                description: signatureText(step.description),
                sequence: step.sequence,
                plannedMinutes: step.plannedMinutes,
                isRequired: step.isRequired,
                defaultAssigneeCitizenId: signatureText(step.defaultAssigneeCitizenId),
                defaultAssigneeName: signatureText(step.defaultAssigneeName),
                tasks: step.tasks.map(task => ({
                    title: signatureText(task.title),
                    detail: signatureText(task.detail),
                    sequence: task.sequence,
                    plannedMinutes: task.plannedMinutes,
                    isRequired: task.isRequired,
                    assigneeCitizenId:
                        signatureText(task.assigneeCitizenId) ||
                        signatureText(step.defaultAssigneeCitizenId),
                    assigneeName:
                        signatureText(task.assigneeName) || signatureText(step.defaultAssigneeName),
                    remark: signatureText(task.remark)
                }))
            }))
        }))
    );

function prepareSections(sections?: RunbookSection[]) {
    if (!sections?.length) return createDefaultSections();

    const next = [...sections];
    const executionIndex = next.findIndex(section => section.sectionType === 'execution');
    if (executionIndex < 0) {
        next.unshift(createDefaultSections()[0]);
    } else if (next[executionIndex].steps.length === 0) {
        next[executionIndex] = { ...next[executionIndex], steps: [createStep(1)] };
    }

    if (!next.some(section => section.sectionType === 'rollback')) {
        next.push(createDefaultSections()[1]);
    }

    return next;
}

function validatePlan(
    run: Partial<MigrationRun>,
    selectedPeople: string[],
    selectedTeams: string[],
    schedule: boolean
) {
    if (!run.title?.trim()) return 'Please enter a migration title.';
    if (schedule && (!run.plannedStartAt || !run.plannedEndAt)) {
        return 'Planned start and end are required before scheduling.';
    }
    if (
        run.plannedStartAt &&
        run.plannedEndAt &&
        new Date(run.plannedEndAt).getTime() <= new Date(run.plannedStartAt).getTime()
    ) {
        return 'Planned end must be after planned start.';
    }
    if (run.visibility === 'selected_people' && selectedPeople.length === 0) {
        return 'Please select at least one person.';
    }
    if (run.visibility === 'selected_teams' && selectedTeams.length === 0) {
        return 'Please select at least one team.';
    }

    const execution = run.sections?.find(section => section.sectionType === 'execution');
    if (!execution?.steps.length) return 'Please add at least one execution step.';

    for (const step of run.sections?.flatMap(section => section.steps) ?? []) {
        if (!step.title.trim()) return 'Every step needs a title.';
        if (!step.tasks.length) return `Please add at least one detail to ${step.title}.`;
        if (step.tasks.some(task => !task.title.trim())) {
            return `Every detail in ${step.title} needs a description.`;
        }
    }

    return '';
}

export default function MigrationRunbookManage() {
    const { runId } = useParams<{ runId: string }>();
    const isNew = !runId || runId === 'new';
    const identity = useRunbookIdentity();
    const loader = useLoader();
    const history = useHistory();
    const [employees, setEmployees] = useState<AuthenticationResponse[]>([]);
    const [run, setRun] = useState<Partial<MigrationRun>>({
        title: '',
        description: '',
        visibility: 'only_me',
        members: [],
        teams: [],
        sections: createDefaultSections()
    });
    const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
    const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

    const canEdit =
        isNew || (run.role === 'run_owner' && ['draft', 'scheduled'].includes(run.status ?? ''));
    const availableTeams = useMemo(
        () =>
            [...new Set(employees.flatMap(employee => normalizeTeams(employee.team)))]
                .filter(Boolean)
                .sort(),
        [employees]
    );

    useEffect(() => {
        getMasterEmployee({ citizenId: '' })
            .then(rows => setEmployees(rows ?? []))
            .catch(error => Swal.fire('Employee error', (error as Error).message, 'error'));
    }, []);

    useEffect(() => {
        if (isNew || !identity.citizenId) return;

        loader.show();
        getRun(runId, identity.citizenId, identity.teams.join(','))
            .then(result => {
                setRun({ ...result, sections: prepareSections(result.sections) });
                setSelectedPeople(
                    result.members
                        .filter(member => member.role !== 'run_owner')
                        .map(member => member.citizenId)
                );
                setSelectedTeams(result.teams.map(team => team.teamName));
            })
            .catch(error => Swal.fire('Load failed', (error as Error).message, 'error'))
            .finally(loader.hide);
    }, [runId, isNew, identity.citizenId, identity.teams.join(',')]);

    const save = async (schedule: boolean) => {
        const validationMessage = validatePlan(run, selectedPeople, selectedTeams, schedule);
        if (validationMessage) {
            await Swal.fire('Check the plan', validationMessage, 'warning');
            return;
        }

        try {
            loader.show();
            const visibility = (run.visibility ?? 'only_me') as Visibility;
            const members = visibility === 'selected_people' ? selectedPeople : [];
            const teams = visibility === 'selected_teams' ? selectedTeams : [];
            const payload = {
                ...actor(identity, run.versionNo ?? 0),
                title: run.title?.trim() ?? '',
                description: run.description?.trim() ?? '',
                plannedStartAt: toApiDate(run.plannedStartAt),
                plannedEndAt: toApiDate(run.plannedEndAt),
                ownerCitizenId: run.ownerCitizenId || identity.citizenId,
                ownerName: run.ownerName || identity.name || identity.citizenId,
                visibility,
                sections: run.sections ?? [],
                members: members.map(citizenId => {
                    const employee = employees.find(item => item.ctizenID === citizenId);
                    return {
                        citizenId,
                        displayName: employee?.nickName || citizenId,
                        role: 'editor' as const
                    };
                }),
                teams: teams.map(teamName => ({ teamName, role: 'viewer' as const }))
            };
            const saved = await saveRun(payload, isNew ? undefined : runId);
            if (planSignature(payload.sections) !== planSignature(saved.sections)) {
                throw new Error(
                    'The server did not return the complete checklist. Your changes are still on this screen; please do not refresh.'
                );
            }

            if (schedule) {
                try {
                    const scheduled = await changeRunStatus(
                        saved.id,
                        'schedule',
                        actor(identity, saved.versionNo)
                    );
                    history.push(`/migration-runbook/runs/${scheduled.id}`);
                } catch (scheduleError) {
                    setRun(saved);
                    history.replace(`/migration-runbook/runs/${saved.id}/manage`);
                    await Swal.fire(
                        'Draft saved, scheduling failed',
                        `${(scheduleError as Error).message} Open this draft and try scheduling again.`,
                        'warning'
                    );
                }
                return;
            }

            setRun(saved);
            history.replace(`/migration-runbook/runs/${saved.id}/manage`);
            await Swal.fire('Plan saved', 'Your migration checklist is ready.', 'success');
        } catch (error) {
            Swal.fire('Save failed', (error as Error).message, 'error');
        } finally {
            loader.hide();
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 px-4 py-6 md:px-6">
            <div className="mx-auto max-w-[1500px]">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <Link
                            to="/migration-runbook"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                            Back to migrations
                        </Link>
                        <h1 className="mt-2 text-2xl font-bold text-slate-950">
                            {isNew ? 'Create migration' : 'Migration workspace'}
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Plan the work, assign owners, then schedule it when the checklist is
                            ready.
                        </p>
                    </div>
                    {run.id && run.status !== 'draft' && (
                        <Link
                            to={`/migration-runbook/runs/${run.id}`}
                            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                        >
                            Open live run
                        </Link>
                    )}
                </div>

                {!canEdit && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        This migration is already {run.status?.replace(/_/g, ' ')}. The plan is
                        read-only.
                    </div>
                )}

                <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
                    <main className="space-y-5">
                        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        Migration details
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        Owner: {run.ownerName || identity.name || 'Current user'}
                                    </p>
                                </div>
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-600">
                                    {run.status || 'new'}
                                </span>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                                <label className="block text-sm font-medium text-gray-700 md:col-span-2">
                                    Migration title <span className="text-red-500">*</span>
                                    <input
                                        disabled={!canEdit}
                                        value={run.title ?? ''}
                                        onChange={event =>
                                            setRun({ ...run, title: event.target.value })
                                        }
                                        placeholder="Example: Move reporting database to new server"
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                                    />
                                </label>
                                <label className="block text-sm font-medium text-gray-700 md:col-span-2">
                                    Objective or context
                                    <textarea
                                        disabled={!canEdit}
                                        value={run.description ?? ''}
                                        onChange={event =>
                                            setRun({ ...run, description: event.target.value })
                                        }
                                        rows={3}
                                        placeholder="Describe the outcome, scope, or important constraints."
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                                    />
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Planned start
                                    <input
                                        disabled={!canEdit}
                                        type="datetime-local"
                                        value={toLocalInput(run.plannedStartAt)}
                                        onChange={event =>
                                            setRun({ ...run, plannedStartAt: event.target.value })
                                        }
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm"
                                    />
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Planned end
                                    <input
                                        disabled={!canEdit}
                                        type="datetime-local"
                                        value={toLocalInput(run.plannedEndAt)}
                                        onChange={event =>
                                            setRun({ ...run, plannedEndAt: event.target.value })
                                        }
                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm"
                                    />
                                </label>
                            </div>
                        </section>

                        <RunbookPlanBuilder
                            sections={run.sections ?? []}
                            employees={employees}
                            disabled={!canEdit}
                            onChange={sections => setRun(previous => ({ ...previous, sections }))}
                        />
                    </main>

                    <aside className="space-y-4 xl:sticky xl:top-4">
                        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Who can access this?
                            </h2>
                            <p className="mt-1 text-xs text-gray-500">
                                This controls the signed-in Migration menu. Calendar public links
                                remain separate.
                            </p>
                            <div className="mt-4 space-y-2">
                                {visibilityOptions.map(option => (
                                    <label
                                        key={option.value}
                                        className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                                            run.visibility === option.value
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <input
                                            disabled={!canEdit}
                                            type="radio"
                                            name="visibility"
                                            checked={run.visibility === option.value}
                                            onChange={() =>
                                                setRun({ ...run, visibility: option.value })
                                            }
                                            className="mt-1 h-4 w-4 text-blue-600"
                                        />
                                        <span>
                                            <span className="block text-sm font-semibold text-gray-800">
                                                {option.title}
                                            </span>
                                            <span className="block text-xs text-gray-500">
                                                {option.detail}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {run.visibility === 'selected_people' && (
                                <div className="mt-4 border-t border-gray-100 pt-4">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                        Select people
                                    </label>
                                    <MultiEmployeePicker
                                        values={selectedPeople}
                                        employees={employees}
                                        disabled={!canEdit}
                                        placeholder="Search name or citizen ID"
                                        onChange={setSelectedPeople}
                                    />
                                </div>
                            )}

                            {run.visibility === 'selected_teams' && (
                                <div className="mt-4 border-t border-gray-100 pt-4">
                                    <p className="mb-2 text-sm font-medium text-gray-700">
                                        Select teams
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {availableTeams.map(team => {
                                            const selected = selectedTeams.includes(team);
                                            return (
                                                <button
                                                    key={team}
                                                    type="button"
                                                    disabled={!canEdit}
                                                    onClick={() =>
                                                        setSelectedTeams(current =>
                                                            selected
                                                                ? current.filter(
                                                                      item => item !== team
                                                                  )
                                                                : [...current, team]
                                                        )
                                                    }
                                                    className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                                                        selected
                                                            ? 'border-blue-600 bg-blue-600 text-white'
                                                            : 'border-gray-300 bg-white text-gray-700'
                                                    }`}
                                                >
                                                    {team}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {availableTeams.length === 0 && (
                                        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                            No teams were found in master employee data.
                                        </p>
                                    )}
                                </div>
                            )}
                        </section>

                        {canEdit && (
                            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => save(false)}
                                    className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                    {run.status === 'scheduled' ? 'Save changes' : 'Save draft'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => save(true)}
                                    className="mt-2 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                                >
                                    {run.status === 'scheduled'
                                        ? 'Save changes and return to live'
                                        : 'Save and schedule'}
                                </button>
                                <p className="mt-3 text-center text-xs text-gray-500">
                                    Scheduling also creates or updates the linked Calendar event.
                                </p>
                            </section>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
}
